const clean = (value) => String(value ?? "").trim();
const key = (value) => clean(value).toLowerCase();

export const isIoIntroducer = (row = {}) =>
  key(row.RecipientType || row.entityType).includes("introducer");

export const ioIntroducerTargets = (rows = []) => {
  const targets = new Map();
  rows.forEach((row) => {
    if (!isIoIntroducer(row)) return;
    const targetId = clean(
      row.RecipientCrmId ?? row.recipientCrmId ?? row.RecipientUserId ?? row.RecipientId,
    );
    if (!targetId) return;
    const name = clean(row.Recipient || row.name) || `IO Introducer ${targetId}`;
    targets.set(key(targetId), {
      id: `introducer:${targetId}`,
      type: "introducer",
      targetId,
      name,
      optionName: `[IO Introducer] ${name} — ${targetId}`,
    });
  });
  return [...targets.values()].sort((left, right) =>
    left.name.localeCompare(right.name, "en-GB", { numeric: true }),
  );
};

export const mappingTargetType = (mapping = {}) =>
  clean(mapping.targetType || mapping.entityType).toLowerCase();

export const mappingTargetId = (mapping = {}) =>
  clean(
    mapping.targetId ||
      mapping.assignedAdviserEmail ||
      mapping.ioIntroducerId,
  );

export const mappedRecipientIdsByAdviser = (mappings = {}) => {
  const ids = new Map();
  Object.values(mappings).forEach((mapping) => {
    if (mappingTargetType(mapping) !== "adviser") return;
    const email = key(mappingTargetId(mapping));
    const sourceId = clean(mapping.recipientCrmId);
    if (!email || !sourceId) return;
    if (!ids.has(email)) ids.set(email, []);
    ids.get(email).push(sourceId);
  });
  return ids;
};

export const aggregateIoTotalsByRecipientId = (rows = []) => {
  const totals = new Map();
  rows.forEach((row) => {
    const recipientId = key(row.adviserId);
    const amount = Number(row.paymentAmount || 0);
    if (!recipientId || !Number.isFinite(amount)) return;
    totals.set(recipientId, (totals.get(recipientId) || 0) + amount);
  });
  return totals;
};

export const ioIntroducerTotal = ({
  totalsByRecipientId = new Map(),
  targetId,
  recipientIds = [],
} = {}) => [...new Set([targetId, ...recipientIds].map(key).filter(Boolean))]
  .reduce(
    (total, recipientId) =>
      total + Number(totalsByRecipientId.get(recipientId) || 0),
    0,
  );

export const mappedIoIntroducerGroups = (rows = [], mappings = {}) => {
  const paymentRowsByRecipient = new Map();
  rows.forEach((row) => {
    const recipientId = key(
      row.RecipientCrmId ?? row.RecipientUserId ?? row.RecipientId,
    );
    if (!recipientId) return;
    if (!paymentRowsByRecipient.has(recipientId)) paymentRowsByRecipient.set(recipientId, []);
    paymentRowsByRecipient.get(recipientId).push(row);
  });

  const groups = new Map();
  Object.values(mappings).forEach((mapping) => {
    if (mappingTargetType(mapping) !== "introducer") return;
    const targetId = mappingTargetId(mapping);
    const sourceId = clean(mapping.recipientCrmId);
    if (!targetId) return;
    const targetKey = key(targetId);
    const current = groups.get(targetKey) || {
      targetId,
      name: clean(mapping.targetName || mapping.ioName || mapping.displayName) ||
        `IO Introducer ${targetId}`,
      recipientIds: new Set([targetId]),
      mappedSources: new Map(),
      total: 0,
      paymentCount: 0,
    };
    if (sourceId) {
      current.recipientIds.add(sourceId);
      if (key(sourceId) !== targetKey) {
        current.mappedSources.set(key(sourceId), {
          recipientCrmId: sourceId,
          sourceName: clean(mapping.sourceName) || `IO Recipient ${sourceId}`,
        });
      }
    }
    groups.set(targetKey, current);
  });

  groups.forEach((group) => {
    group.recipientIds.forEach((recipientId) => {
      (paymentRowsByRecipient.get(key(recipientId)) || []).forEach((row) => {
        group.total += Number(String(row.PaymentAmount ?? 0).replace(/[,£]/g, "")) || 0;
        group.paymentCount += 1;
      });
    });
  });

  return [...groups.values()].map((group) => ({
    ...group,
    recipientIds: [...group.recipientIds],
    mappedSources: [...group.mappedSources.values()],
  })).sort((left, right) => left.name.localeCompare(right.name, "en-GB", { numeric: true }));
};
